import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Checkbox, Divider } from "semantic-ui-react";

import IPriestProfileData from "../../data/priest/PriestProfileData";
import { mapToExportedPriestData } from "../../util/csvExportUtils/exportMapperFunctions";
import { toCSV } from "../../util/csvExportUtils/exportToCSV";

interface Props {
    priests: IPriestProfileData[];
    open: boolean;
    onClose: () => void;
}

const ExportPriestDialog: React.FC<Props> = ({ priests, open, onClose }) => {
    const [fields, setFields] = useState<string[]>([]);
    const [selected, setSelected] = useState<Record<string, boolean>>({});

    const sampleMapped = useMemo(() => {
        if (!priests || priests.length === 0) return {};
        return mapToExportedPriestData(priests[0]);
    }, [priests]);

    useEffect(() => {
        const keys = Object.keys(sampleMapped || {});
        setFields(keys);
        const initial: Record<string, boolean> = {};
        keys.forEach((k) => (initial[k] = true));
        setSelected(initial);
    }, [sampleMapped]);

    const toggleField = (field: string) => {
        setSelected((s) => ({ ...s, [field]: !s[field] }));
    };

    const selectAll = (value: boolean) => {
        const newSel: Record<string, boolean> = {};
        fields.forEach((f) => (newSel[f] = value));
        setSelected(newSel);
    };

    const exportSelected = () => {
        if (!priests || priests.length === 0) return;
        const rows = priests.map((p) => mapToExportedPriestData(p));
        const chosenKeys = fields.filter((f) => selected[f]);
        const filteredRows = rows.map((row) => {
            const out: any = {};
            chosenKeys.forEach((k) => {
                out[k] = (row as any)[k];
            });
            return out;
        });
        toCSV().exportAsCSVFile(filteredRows).saveAsCSVFile("exportedPriestData_selected");
        onClose();
    };

    return (
        <Modal closeIcon onClose={onClose} open={open} size="small">
            <Modal.Header>Select fields to export</Modal.Header>
            <Modal.Content>
                <div style={{ marginBottom: 8 }}>
                    <Button compact onClick={() => selectAll(true)}>
                        Select all
                    </Button>
                    <Button compact onClick={() => selectAll(false)} style={{ marginLeft: 8 }}>
                        Deselect all
                    </Button>
                </div>
                <Divider />
                <div style={{ maxHeight: 360, overflow: "auto", paddingRight: 8 }}>
                    {fields.map((field) => (
                        <div key={field} style={{ marginBottom: 6 }}>
                            <Checkbox checked={!!selected[field]} label={field} onChange={() => toggleField(field)} />
                        </div>
                    ))}
                </div>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose}>Close</Button>
                <Button color="green" disabled={!priests || priests.length === 0} onClick={exportSelected}>
                    Export selected
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default ExportPriestDialog;
